import * as styles from "./PeopleGroup.css";

import type { JSX, ReactNode } from "react";

import { Icon } from "@frontend/components/atoms/Icon";

import { PersonCardSkeleton } from "./PersonCardSkeleton";

export type PeopleGroupsProps = {
  children: ReactNode;
};

/** The lists of people a record names, one under the other. */
export function PeopleGroups({ children }: PeopleGroupsProps): JSX.Element {
  return <div className={styles.groups}>{children}</div>;
}

export type PeopleGroupProps = {
  title: ReactNode;
  count: number;
  empty: ReactNode;
  addLabel: ReactNode;
  /** The list the cards read their people from has not arrived yet. */
  loading?: boolean;
  /** Nobody left to name: the list the window would offer is empty. */
  addDisabled?: boolean;
  onAdd: (origin: DOMRect) => void;
  action?: ReactNode;
  children: ReactNode;
};

/** A titled grid of cards, the card that adds one always last. */
export function PeopleGroup({
  title,
  count,
  empty,
  addLabel,
  loading,
  addDisabled,
  onAdd,
  action,
  children,
}: PeopleGroupProps): JSX.Element {
  return (
    <div className={styles.group}>
      <div className={styles.header}>
        <div className={styles.title}>
          <span>{title}</span>

          {0 < count ? <span className={styles.count}>{count}</span> : null}
        </div>

        {action}
      </div>

      <div className={styles.grid}>
        {0 === count ? (
          <p className={styles.empty}>{empty}</p>
        ) : loading ? (
          // A card is only half itself until its list lands, so it waits as
          // its own shape rather than filling in under the reader.
          Array.from({ length: count }, (_unused, index) => (
            <PersonCardSkeleton key={index} />
          ))
        ) : (
          children
        )}

        <button
          type="button"
          className={styles.addCard}
          disabled={addDisabled}
          onClick={(event) =>
            onAdd(event.currentTarget.getBoundingClientRect())
          }
        >
          <Icon name="Plus" size="sm" />
          {addLabel}
        </button>
      </div>
    </div>
  );
}
